import React from 'react';
import { FiUser } from 'react-icons/fi';
import { formatWhatsAppText } from '../utils/whatsappFormatting';

interface ContactItemProps {
  contact: {
    id: number;
    name: string;
    number: string;
    email?: string;
    avatarUrl: string | null;
    lastMessage?: string;
    lastMessageTime?: string;
    tagId?: number | null;
    tagColor?: string;
    responsibleName?: string | null;
  };
  isSelected: boolean;
  onSelect: () => void;
  onAvatarClick?: (url: string, name: string) => void;
  hasNewMessage?: boolean;
  onClearNewMessage?: (number: string) => void;
  showArchivedIndicator?: boolean;
}

const formatPhoneNumber = (phone: string): string => {
  const numbers = phone.replace(/\D/g, '');

  if (numbers.length === 13 && numbers.startsWith('55')) {
    return `+55 (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9)}`;
  } else if (numbers.length === 11) {
    return `+55 (${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  } else if (numbers.length === 10) {
    return `+55 (${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  }

  return phone.startsWith('+55') ? phone : `+55 ${phone}`;
};

export const ContactItem: React.FC<ContactItemProps> = ({
  contact,
  isSelected,
  onSelect,
  onAvatarClick,
  hasNewMessage = false,
  onClearNewMessage,
  showArchivedIndicator = false
}) => {
  const handleClick = () => {
    if (hasNewMessage && onClearNewMessage) {
      onClearNewMessage(contact.number);
    }
    onSelect();
  };

  const hasAvatar = !!contact.avatarUrl;

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (contact.avatarUrl) onAvatarClick?.(contact.avatarUrl, contact.name);
  };

  const handleAvatarKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (contact.avatarUrl) onAvatarClick?.(contact.avatarUrl, contact.name);
    }
  };

  return (
    <div
      className={`contact-item ${isSelected ? 'selected' : ''} ${hasNewMessage ? 'has-new-message' : ''} ${showArchivedIndicator ? 'is-archived' : ''}`}
      onClick={handleClick}
    >
      <div className={`contact-avatar ${hasAvatar && onAvatarClick ? 'contact-avatar-clickable' : ''}`}>
        {hasAvatar ? (
          <img
            src={contact.avatarUrl!}
            alt={contact.name}
            style={contact.tagId ? { borderColor: contact.tagColor } : undefined}
            {...(onAvatarClick && {
              onClick: handleAvatarClick,
              onKeyDown: handleAvatarKeyDown,
              role: 'button',
              tabIndex: 0
            })}
          />
        ) : (
          <div className="contact-avatar-placeholder" aria-hidden>
            <FiUser size={20} />
          </div>
        )}
        {hasNewMessage && (
          <div className="new-message-indicator" />
        )}
      </div>

      <div className="contact-info">
        <span className="contact-name">{contact.name}</span>
        <div className="contact-meta">
          <span className="message-preview contact-last" title={contact.lastMessage || undefined}>
            {contact.lastMessage ? formatWhatsAppText(contact.lastMessage) : 'Sem mensagens'}
          </span>
          {showArchivedIndicator && (
            <span className="contact-archived-badge" title="Contato arquivado">
              Arquivado
            </span>
          )}
          {contact.responsibleName && (
            <span
              className="contact-responsible-badge"
              title={`Responsável: ${contact.responsibleName}`}
            >
              {contact.responsibleName.split(' ')[0]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}; 